import { useEffect, useMemo, useState } from 'react';
import { Command } from 'cmdk';
import { useNavigate, useRouterState } from '@tanstack/react-router';
import { useQueryClient } from '@tanstack/react-query';
import { FolderKanban, Search, Sparkles, type LucideIcon } from 'lucide-react';
import type { Project } from '@seo/types';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from '@/components/ui/dialog';
import { useFeatureFlags } from '@/lib/feature-flags';
import {
  SECTIONS,
  SETTINGS_ITEMS,
  matchDetail,
  type SectionGroup,
} from '@/lib/nav-registry';
import { cn } from '@/lib/cn';

interface PaletteItem {
  id: string;
  label: string;
  hint?: string;
  icon: LucideIcon;
  to: string;
  params?: Record<string, string>;
  keywords?: string[];
}

interface CommandPaletteProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const GROUP_HINTS: Record<SectionGroup, string> = {
  workspace: 'Workspace',
  research: 'Research',
  content: 'Content',
  strategy: 'Strategy',
};

/**
 * The project the user is currently working in: either the URL is the
 * project page itself, or the current detail entity's `project_id` is in
 * the query cache (same registry lookup the breadcrumb uses).
 */
function useCurrentProject(): { id: string; name: string | null } | null {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const qc = useQueryClient();

  const detail = matchDetail(pathname);
  if (!detail) return null;

  let projectId: string | null = null;
  if (detail.kind === 'project') {
    projectId = detail.entityId;
  } else {
    const entity = qc.getQueryData<{ project_id?: string }>(
      detail.queryKey as unknown[],
    );
    projectId = entity?.project_id ?? null;
  }
  if (!projectId) return null;
  const project = qc.getQueryData<Project>(['project', projectId]);
  return { id: projectId, name: project?.name ?? null };
}

export function CommandPalette({
  open,
  onOpenChange,
}: CommandPaletteProps): JSX.Element {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const flags = useFeatureFlags();
  const currentProject = useCurrentProject();

  useEffect(() => {
    if (!open) setSearch('');
  }, [open]);

  // Derived from the shared registry, so labels, feature flags, and the set
  // of destinations always match the sidebar (settings pages included).
  const navItems: PaletteItem[] = useMemo(() => {
    const sections: PaletteItem[] = SECTIONS.filter(
      (s) => !s.featureFlag || flags[s.featureFlag],
    ).map((s) => ({
      id: `nav:${s.path}`,
      label: s.label,
      hint: GROUP_HINTS[s.group],
      icon: s.icon,
      to: s.path,
      keywords: s.keywords,
    }));
    const settings: PaletteItem[] = SETTINGS_ITEMS.map((s) => ({
      id: `nav:${s.path}`,
      label: s.label,
      hint: 'Settings',
      icon: s.icon,
      to: s.path,
      keywords: s.keywords,
    }));
    return [...sections, ...settings];
  }, [flags]);

  const contextItems: PaletteItem[] = useMemo(() => {
    if (!currentProject) return [];
    return [
      {
        id: 'ctx:current-project',
        label: currentProject.name
          ? `Open project: ${currentProject.name}`
          : 'Open current project',
        hint: currentProject.name ? undefined : `…${currentProject.id.slice(0, 6)}`,
        icon: FolderKanban,
        to: '/projects/$projectId',
        params: { projectId: currentProject.id },
        keywords: ['this', 'open', 'project'],
      },
    ];
  }, [currentProject]);

  function run(item: PaletteItem): void {
    onOpenChange(false);
    // Registry paths are computed strings; the registry guarantees they
    // exist in the route table (single sanctioned cast, see nav-registry).
    if (item.params) {
      void navigate({ to: item.to, params: item.params } as unknown as {
        to: string;
      });
    } else {
      void navigate({ to: item.to } as unknown as { to: string });
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className={cn(
          'max-w-xl gap-0 overflow-hidden border-border/70 bg-popover/95 p-0 backdrop-blur-2xl',
          'shadow-elevation-lg',
        )}
      >
        <DialogTitle className="sr-only">Command palette</DialogTitle>
        <DialogDescription className="sr-only">
          Search and jump to a page or run a quick action.
        </DialogDescription>
        <Command
          label="Command palette"
          className="flex h-[440px] w-full flex-col"
          loop
        >
          <div className="flex items-center gap-2 border-b border-border/70 px-3.5">
            <Search className="h-4 w-4 text-muted-foreground" />
            <Command.Input
              autoFocus
              value={search}
              onValueChange={setSearch}
              placeholder="Type a command or search…"
              className="h-12 flex-1 bg-transparent text-sm outline-none placeholder:text-muted-foreground"
            />
            <kbd className="hidden rounded border border-border/70 bg-muted px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground sm:inline-block">
              ESC
            </kbd>
          </div>

          <Command.List className="flex-1 overflow-y-auto p-2">
            <Command.Empty className="flex h-24 items-center justify-center text-sm text-muted-foreground">
              No results.
            </Command.Empty>

            {contextItems.length > 0 ? (
              <Command.Group
                heading="Current context"
                className="text-xs font-medium text-muted-foreground [&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:py-1.5"
              >
                {contextItems.map((item) => (
                  <PaletteRow key={item.id} item={item} onRun={run} />
                ))}
              </Command.Group>
            ) : null}

            <Command.Group
              heading="Navigate"
              className="text-xs font-medium text-muted-foreground [&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:py-1.5"
            >
              {navItems.map((item) => (
                <PaletteRow key={item.id} item={item} onRun={run} />
              ))}
            </Command.Group>
          </Command.List>

          <div className="flex items-center justify-between border-t border-border/70 px-3 py-2 text-[11px] text-muted-foreground">
            <span className="flex items-center gap-1.5">
              <Sparkles className="h-3 w-3" /> SEO AI Agent
            </span>
            <span className="flex items-center gap-2">
              <kbd className="rounded border border-border/70 bg-muted px-1.5 py-0.5">
                ↵
              </kbd>
              <span>to select</span>
              <kbd className="ml-2 rounded border border-border/70 bg-muted px-1.5 py-0.5">
                ↑↓
              </kbd>
              <span>to navigate</span>
            </span>
          </div>
        </Command>
      </DialogContent>
    </Dialog>
  );
}

function PaletteRow({
  item,
  onRun,
}: {
  item: PaletteItem;
  onRun: (item: PaletteItem) => void;
}): JSX.Element {
  const Icon = item.icon;
  return (
    <Command.Item
      value={`${item.label} ${item.hint ?? ''} ${item.keywords?.join(' ') ?? ''}`}
      onSelect={() => onRun(item)}
      className={cn(
        'flex cursor-pointer items-center gap-3 rounded-md px-2.5 py-2 text-sm text-foreground/90 outline-none',
        'data-[selected=true]:bg-accent data-[selected=true]:text-accent-foreground',
        'transition-colors',
      )}
    >
      <span className="flex h-7 w-7 items-center justify-center rounded-md border border-border/70 bg-muted/50">
        <Icon className="h-3.5 w-3.5" />
      </span>
      <span className="flex-1 truncate">{item.label}</span>
      {item.hint ? (
        <span className="text-xs text-muted-foreground">{item.hint}</span>
      ) : null}
    </Command.Item>
  );
}

/**
 * Global ⌘K / Ctrl+K keyboard handler. Mount once at the AppShell level.
 */
export function useCommandPaletteHotkey(toggle: () => void): void {
  useEffect(() => {
    function onKey(e: KeyboardEvent): void {
      if ((e.key === 'k' || e.key === 'K') && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        toggle();
      }
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [toggle]);
}
